import _ from 'lodash';

import { getLogger } from '@nerdwallet/logging';
import fetchAlpsMarketplaceEntityIdOrdering from './fetch';
import log from '../log';

/**
 * Keys under which with-data-dependencies data is provided.
 * @private - Exported for testing.
 */
export const ALPS_DATA_KEYS = {
  PRODUCTS: 'marketplaceEntities',
  IDS: 'ids',
  RESULT_SIZE_LIMIT: 'resultSizeLimit',
  MODEL_EXECUTION_IDS: 'modelExecutionIds',
};

export const alpsLogger = getLogger('ALPS', log);

/**
 * Retrieve alps provided ids data.
 *
 * @param {Object} data - The "withDataDependencies" mapped data.
 * @returns {Array<String>} - The requested ids.
 */
export const getAlpsDataIds = ({ value } = {}) =>
  _.get(value, ALPS_DATA_KEYS.IDS, []);

/**
 * Retrieve alps provided list of model execution ids for personalized responses.
 *
 * @param {Object} data - The "withDataDependencies" mapped data.
 * @returns {Array<String>} - The model execution ids.
 */
export const getAlpsDataModelExecutionIds = ({ value } = {}) =>
  value?.[ALPS_DATA_KEYS.MODEL_EXECUTION_IDS];

/**
 * Retrieve alps provided product data.
 *
 * @param {Object} data - The "withDataDependencies" mapped data.
 * @returns {Object<String:Object>} - The product data object, keyed by marketplace id.
 */
export const getAlpsDataProducts = ({ value } = {}) =>
  _.get(value, ALPS_DATA_KEYS.PRODUCTS, {});

/**
 * Retrieve alps provided list of results to show.
 *
 * @param {Object} data - The "withDataDependencies" mapped data.
 * @returns {Integer} - The number of results to show.
 */
export const getAlpsDataResultSizeLimit = ({ value } = {}) =>
  value?.[ALPS_DATA_KEYS.RESULT_SIZE_LIMIT] ?? 100;

/**
 * Curried, generic ALPS provider for the following data flow:
 * ALPS -> Marketplace* Service -> Data provider for <Table />
 * To be bound by product fetch and product type.
 * @private - Not to be used directly.
 *
 * @callback fetchMarketplaceEntitiesById
 * @param {Array<String>} marketplaceEntityIds - Flattened array of marketplace uuids.
 * @returns {Promise(Object<String: Object>)} - Data mapping UUID -> "Marketplace Entity" object.
 *
 * @callback createProvider
 * @param {Object} options
 * @param {Object} [options.req]
 * @param {...Object} options.fetchOptions
 * @returns {Promise({ ids: <Array>, products: <Object> })}
 *
 * @param {fetchMarketplaceEntitiesById} fetchMarketplaceEntitiesById
 * @param {String} productType
 * @returns {createProvider} - The function to be passed to the StructuredContent client.
 */
export const createProductTypeBoundCreateProvider =
  (fetchMarketplaceEntitiesById, productType) => (fetchOptions) => {
    const { req } = fetchOptions;
    const loadDescription = async ({
      ids: defaultIds,
      productPage,
      ...props
    }) => {
      const { ids, modelExecutionIds, resultSizeLimit } =
        await fetchAlpsMarketplaceEntityIdOrdering({
          defaultIds,
          logger: alpsLogger,
          productPage,
          productType,
          req,
        });

      const marketplaceEntities = await fetchMarketplaceEntitiesById(ids, {
        ...props,
        ...fetchOptions,
      });

      return {
        [ALPS_DATA_KEYS.IDS]: ids,
        [ALPS_DATA_KEYS.MODEL_EXECUTION_IDS]: modelExecutionIds,
        [ALPS_DATA_KEYS.PRODUCTS]: marketplaceEntities,
        [ALPS_DATA_KEYS.RESULT_SIZE_LIMIT]: resultSizeLimit,
      };
    };

    const load = (descriptions) => descriptions.map(loadDescription);
    return { load };
  };
